/**
 * Base HTTP client for Durin AI service
 */

import {
  ClientConfig,
  UnifiedResponse,
  StreamingChunk,
  ModelInfo,
  HealthResponse,
  TunnelRequest,
  IntentRequest
} from '../types';
import { DurinError, DurinNetworkError, handleHttpError, isNetworkError, createStreamIterable } from '../utils';
import { DEFAULT_TIMEOUT } from '../constants';

export class BaseClient {
  protected baseUrl: string;
  protected defaultModelId?: string;
  protected timeout: number;
  protected headers: Record<string, string>;
  protected authTokenProvider: (() => string | null) | undefined;
  protected schoolId: string;

  constructor(config: ClientConfig) {
    // Validate required schoolId
    if (!config.schoolId || config.schoolId.trim() === '') {
      throw new Error('schoolId is required and cannot be empty');
    }

    const defaultBaseUrl = process.env['DURIN_API_URL'];
    this.baseUrl = (config.baseUrl || defaultBaseUrl || '').replace(/\/$/, ''); // Remove trailing slash

    if (config.defaultModelId !== undefined) {
      this.defaultModelId = config.defaultModelId;
    }
    this.timeout = config.timeout || DEFAULT_TIMEOUT;
    this.headers = {
      'Content-Type': 'application/json',
      ...config.headers
    };
    this.authTokenProvider = config.authTokenProvider;
    this.schoolId = config.schoolId.trim();
  }

  /**
   * Get request headers with optional Authorization header
   */
  protected getRequestHeaders(): Record<string, string> {
    const headers = { ...this.headers };

    if (this.authTokenProvider) {
      const authToken = this.authTokenProvider();
      if (authToken) {
        headers['Authorization'] = authToken;
      }
    }

    // Add school ID to headers
    headers['X-SITE-ID'] = `PCO:SITE:${this.schoolId}`;

    // Add app identification headers
    headers['X-APP-ID'] = 'PCO';
    headers['X-CLIENT-NAME'] = 'Web';

    return headers;
  }

  /**
   * Make a non-streaming HTTP request
   */
  protected async request<T>(method: string, endpoint: string, body?: any, options?: RequestInit): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    const requestOptions: RequestInit = {
      method,
      headers: this.getRequestHeaders(),
      signal: options?.signal || controller.signal,
      ...options
    };

    if (body && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
      requestOptions.body = JSON.stringify(body);
    }

    try {
      const response = await fetch(url, requestOptions);
      clearTimeout(timeoutId); // Clear timeout on successful fetch

      if (!response.ok) {
        let errorData;
        try {
          const contentType = response.headers.get('content-type');
          if (contentType?.includes('application/json')) {
            errorData = await response.json();
          } else {
            errorData = await response.text();
          }
        } catch {
          // Ignore parse errors for error responses
        }

        throw handleHttpError(response, errorData);
      }

      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        return await response.json();
      } else {
        return ((await response.text()) as unknown) as T;
      }
    } catch (error) {
      clearTimeout(timeoutId); // Clear timeout on error

      if (error instanceof DurinError) {
        throw error;
      }

      if (isNetworkError(error as Error)) {
        throw new DurinNetworkError(`Failed to connect to ${url}`, error as Error);
      }

      throw new DurinError(`Request failed: ${(error as Error).message}`, undefined, undefined, error as Error);
    }
  }

  /**
   * Make a streaming HTTP request
   */
  protected async requestStream(
    endpoint: string,
    body: any,
    isNDJSON = false,
    signal?: AbortSignal
  ): Promise<AsyncIterable<StreamingChunk>> {
    const url = `${this.baseUrl}${endpoint}`;

    // Create abort controller for timeout if no signal provided
    let controller: AbortController | undefined;
    let timeoutId: NodeJS.Timeout | undefined;

    if (!signal) {
      controller = new AbortController();
      timeoutId = setTimeout(() => controller!.abort(), this.timeout);
      signal = controller.signal;
    }

    const requestOptions: RequestInit = {
      method: 'POST',
      headers: this.getRequestHeaders(),
      body: JSON.stringify(body),
      signal
    };

    try {
      const response = await fetch(url, requestOptions);
      if (timeoutId) clearTimeout(timeoutId); // Clear timeout on successful fetch

      if (!response.ok) {
        let errorData;
        try {
          const contentType = response.headers.get('content-type');
          if (contentType?.includes('application/json')) {
            errorData = await response.json();
          } else {
            errorData = await response.text();
          }
        } catch {
          // Ignore parse errors for error responses
        }

        throw handleHttpError(response, errorData);
      }

      if (!response.body) {
        throw new DurinError('No response body for streaming request');
      }

      return createStreamIterable(response.body, isNDJSON, signal);
    } catch (error) {
      if (timeoutId) clearTimeout(timeoutId); // Clear timeout on error

      if (error instanceof DurinError) {
        throw error;
      }

      if (isNetworkError(error as Error)) {
        throw new DurinNetworkError(`Failed to connect to streaming endpoint ${url}`, error as Error);
      }

      throw new DurinError(
        `Streaming request failed: ${(error as Error).message}`,
        undefined,
        undefined,
        error as Error
      );
    }
  }

  /**
   * Health check endpoint
   */
  async healthCheck(): Promise<HealthResponse> {
    return this.request<HealthResponse>('GET', '/health');
  }

  /**
   * Get available models
   */
  async getAvailableModels(): Promise<Record<string, ModelInfo>> {
    return this.request<Record<string, ModelInfo>>('GET', '/v1/prospecting');
  }

  /**
   * Generic tunnel request (non-streaming)
   */
  async tunnel(request: TunnelRequest): Promise<UnifiedResponse> {
    const body: any = {
      prompt: request.prompt,
      stream: false,
      ndjson: false
    };

    // Only include modelId if provided (let backend handle default)
    if (request.modelId || this.defaultModelId) {
      body.modelId = request.modelId || this.defaultModelId;
    }

    // Only include maxTokens if provided
    if (request.maxTokens !== undefined) {
      body.maxTokens = request.maxTokens;
    }

    return this.request<UnifiedResponse>('POST', '/v1/tunnel', body);
  }

  /**
   * Generic tunnel request (streaming)
   */
  async tunnelStream(request: TunnelRequest, signal?: AbortSignal): Promise<AsyncIterable<StreamingChunk>> {
    const body: any = {
      prompt: request.prompt,
      stream: true,
      ndjson: request.ndjson || false
    };

    // Only include modelId if provided (let backend handle default)
    if (request.modelId || this.defaultModelId) {
      body.modelId = request.modelId || this.defaultModelId;
    }

    // Only include maxTokens if provided
    if (request.maxTokens !== undefined) {
      body.maxTokens = request.maxTokens;
    }

    return this.requestStream('/v1/tunnel', body, body.ndjson, signal);
  }

  /**
   * Intent-based request (non-streaming)
   */
  async intentRequest(endpoint: string, request: IntentRequest): Promise<UnifiedResponse> {
    const body: any = {
      prompt: request.prompt,
      parameters: request.parameters,
      stream: false,
      ndjson: false
    };

    // Only include modelId if provided (let backend handle default)
    if (request.modelId || this.defaultModelId) {
      body.modelId = request.modelId || this.defaultModelId;
    }

    // Only include maxTokens if provided
    if (request.maxTokens !== undefined) {
      body.maxTokens = request.maxTokens;
    }

    return this.request<UnifiedResponse>('POST', endpoint, body);
  }

  /**
   * Intent-based request (streaming)
   */
  async intentRequestStream(
    endpoint: string,
    request: IntentRequest,
    signal?: AbortSignal
  ): Promise<AsyncIterable<StreamingChunk>> {
    const body: any = {
      prompt: request.prompt,
      parameters: request.parameters,
      stream: true,
      ndjson: request.ndjson || false
    };

    // Only include modelId if provided (let backend handle default)
    if (request.modelId || this.defaultModelId) {
      body.modelId = request.modelId || this.defaultModelId;
    }

    // Only include maxTokens if provided
    if (request.maxTokens !== undefined) {
      body.maxTokens = request.maxTokens;
    }

    return this.requestStream(endpoint, body, body.ndjson, signal);
  }
}
